import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { HEAD_ROW, TH, TD, ROW, SELECT, BADGE, TITLE, HINT, EMPTY, ERROR_BOX, ACCENT_BTN } from './adminStyles';
import { billingService } from '../../services/billingService';
import { logUiEvent } from '../../services/uiLogger';

const PAGE_SIZE = 20;

// Заявки на пробный месяц (лид-магнит «месяц Малого за реквизиты»).
// Отдельно от «Заявок на счёт»: там путь через деньги (new → invoiced → paid →
// activated), здесь денег нет вовсе — new → activated либо new → rejected.
// Зеркало TRIAL_TRANSITIONS в bit-billing/app/invoices.py.
const STATUS = {
  new: { label: 'Новая', badge: 'bg-paper-bg text-paper-ink-2' },
  activated: { label: 'Тариф выдан', badge: 'bg-paper-panel text-paper-accent' },
  rejected: { label: 'Отклонена', badge: 'bg-paper-track text-paper-ink-3' },
};

export default function TrialRequestsTab() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingService.adminGetTrialRequests({
        page, page_size: PAGE_SIZE, ...(statusFilter ? { status: statusFilter } : {}),
      });
      setItems(data.items || []);
      setTotalCount(data.total_count || 0);
    } catch (e) {
      setError('Не удалось загрузить заявки');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const activate = async (item) => {
    setBusyId(item.id);
    setError(null);
    try {
      await billingService.adminActivateTrial(item.id);
      logUiEvent('trial_activated', { component: 'admin' });
      await load();
    } catch (e) {
      // 409 сюда приходит, если кто-то уже нажал кнопку: показываем текст,
      // а не молчим — иначе непонятно, выдан тариф или нет.
      setError(e.response?.data?.detail || 'Не удалось выдать тариф');
    } finally {
      setBusyId(null);
    }
  };

  // Отказ. Причину спрашиваем: заявка терминальна, и через месяц по одному
  // статусу уже не восстановить, почему её закрыли.
  const reject = async (item) => {
    // eslint-disable-next-line no-alert
    const note = window.prompt(`Отклонить заявку «${item.company_name}»? Причина:`, '');
    if (note === null) return;
    setBusyId(item.id);
    setError(null);
    try {
      await billingService.adminRejectTrial(item.id, note.trim());
      logUiEvent('trial_rejected', { component: 'admin' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось отклонить заявку');
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h3 className={TITLE}>Заявки на пробный месяц</h3>
        <select
          data-testid="trial-status-filter"
          className={SELECT}
          value={statusFilter}
          onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
        >
          <option value="">Все статусы</option>
          {Object.entries(STATUS).map(([code, s]) => (
            <option key={code} value={code}>{s.label}</option>
          ))}
        </select>
        <span className={HINT}>{totalCount} шт.</span>
      </div>

      {error && <div data-testid="trial-admin-error" className={`${ERROR_BOX} mb-3`}>{error}</div>}
      {loading && <div className={EMPTY}>Загрузка...</div>}

      {!loading && items.length === 0 && (
        <div className={EMPTY}>Заявок нет</div>
      )}

      {!loading && items.length > 0 && (
        <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className={TH}>Организация</th>
              <th className={TH}>ИНН</th>
              <th className={TH}>Контакт</th>
              <th className={TH}>Статус</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid="trial-row" className={`${ROW} align-top`}>
                <td className={TD}>
                  <div>{item.company_name}</div>
                  {item.comment && <div className={`${HINT} mt-1`}>{item.comment}</div>}
                </td>
                <td className={`${TD} whitespace-nowrap text-paper-ink-3 tabular-nums`}>{item.inn}</td>
                <td className={TD}>
                  <div>{item.contact_name}</div>
                  <div className="text-xs text-paper-ink-3">{item.email}</div>
                  {item.contact_phone && (
                    <div className="text-xs text-paper-ink-3">{item.contact_phone}</div>
                  )}
                </td>
                <td className={TD}>
                  <span className={`${BADGE} ${STATUS[item.status]?.badge || ''}`}>
                    {STATUS[item.status]?.label || item.status}
                  </span>
                </td>
                <td className={`${TD} text-right`}>
                  {item.status === 'new' && (
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        data-testid={`trial-activate-${item.id}`}
                        size="sm"
                        className={ACCENT_BTN}
                        disabled={busyId === item.id}
                        onClick={() => activate(item)}
                      >
                        {busyId === item.id ? 'Выдаём…' : 'Выдать «Малый» на месяц'}
                      </Button>
                      <Button
                        data-testid={`trial-reject-${item.id}`}
                        variant="outline"
                        size="sm"
                        className="text-paper-accent"
                        disabled={busyId === item.id}
                        onClick={() => reject(item)}
                      >
                        Отклонить
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4 text-sm">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Назад
          </Button>
          <span className="text-paper-ink-3">{page} / {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Вперёд
          </Button>
        </div>
      )}
    </div>
  );
}
